import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation('home');

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const stats = [
    { number: '7+', labelKey: 'hero.stats.projects' },
    { number: '150+', labelKey: 'hero.stats.activeUsers' },
    { number: '2+', labelKey: 'hero.stats.years' },
  ];

  return (
    <section className="relative bg-gradient-to-br from-[#03271d] via-[#06382b] to-[#021711] overflow-hidden min-h-screen flex items-center mb-20 -mt-20 pt-20" data-section="hero">
      {/* Grainy Texture Overlay - More static-like */}
      <div
        className="absolute inset-0 opacity-[0.4] mix-blend-soft-light hidden md:block"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='3.5' numOctaves='1' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '100px 100px',
        }}
      />

      {/* Light ray effect behind image - Brighter and larger */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute top-1/2 right-0 -translate-y-1/2 w-[65%] h-[95%] bg-gradient-to-l from-[#1ee59d]/30 via-[#0fa371]/18 to-transparent blur-[120px] mix-blend-screen"
        />
      </div>

      {/* Subtle gradient vignette */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/30" />

      {/* Background Elements - More subtle glow effects */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            x: [0, 50, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-400/5 to-teal-300/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
            x: [0, -50, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-400/5 to-emerald-300/5 rounded-full blur-3xl"
        />
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full h-full flex items-start pt-12 lg:pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-0 items-start min-h-screen">
          {/* Left Side - Text Content */}
          <div className="lg:col-span-5 space-y-8 text-left flex flex-col justify-start pt-8 pb-20 lg:py-20">
            {/* Overline */}
            <motion.div variants={itemVariants}>
              <p className="text-emerald-300 font-semibold text-sm uppercase tracking-widest">
                {t('hero.overline')}
              </p>
            </motion.div>

            {/* Main Headline */}
            <motion.div variants={itemVariants} className="-mt-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
                {t('hero.headlineLine1')} <br />
                {t('hero.headlineLine2')} <br />
                <span className="text-emerald-300 lg:whitespace-nowrap">
                  {t('hero.headlineLine3')}
                </span>
              </h1>
            </motion.div>

            {/* Description */}
            <motion.div variants={itemVariants}>
              <p className="text-lg sm:text-xl text-emerald-100/80 leading-relaxed max-w-xl">
                {t('hero.description')}
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-emerald-400 text-[#0a3d2e] rounded-lg font-semibold transition-all hover:shadow-lg hover:bg-emerald-300 text-center"
              >
                {t('hero.ctaStart')}
              </motion.a>
              <motion.a
                href="#cases"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-all text-center"
              >
                {t('hero.ctaWork')}
              </motion.a>
            </motion.div>

            {/* Stats Row */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-4 sm:gap-6 pt-8 border-t border-white/20 max-w-lg"
            >
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-white">{stat.number}</p>
                  <p className="text-xs sm:text-sm text-emerald-100/70 mt-1">{t(stat.labelKey)}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Side - Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 hidden lg:flex items-start justify-end min-h-screen pt-20 pl-12"
          >
            <img
              src="/images/landing/trial-2.webp"
              alt="Dashboard Preview"
              className="w-[130%] h-auto translate-x-10 lg:translate-x-20 xl:translate-x-32"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
